"use client";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAnalytics, getEvent } from "@/lib/analytics";
import { useSubmitFeedback } from "../model/use-submit-feedback";
import { useUploadFeedbackScreenshots } from "../model/use-upload-feedback-screenshots";
import { useAnnouncementStore } from "../model/use-announcement-store";
import { FEEDBACK_AREAS, feedbackAreaForRoute, resolveAreaLabel } from "@/shared/feedback/feedback-areas";
import { isAddonFeature } from "@/domain/features";
import { toast } from "sonner";
import { Select, SelectItem, SelectTrigger } from "@/components/ui/select";
import { GlassCTAButtonFull, GlassSelectContent, GlassTextarea } from "@/shared/ui/glass";

// ============================================================================
// UI-level types (not domain enums — mapping happens in Stage 2)
// ============================================================================

type Mood = "love" | "good" | "okay" | "bad";
type Category = "bug" | "feature" | "general" | "other" | "praise" | "usability";
interface FileWithPreview {
  readonly file: File;
  readonly dataUrl: string;
}
const MOODS: {
  key: Mood;
  emoji: string;
  label: string;
}[] = [{
  key: "love",
  emoji: "\u{1F60D}",
  label: "Love it"
}, {
  key: "good",
  emoji: "\u{1F60A}",
  label: "Good"
}, {
  key: "okay",
  emoji: "\u{1F610}",
  label: "Okay"
}, {
  key: "bad",
  emoji: "\u{1F61E}",
  label: "Bad"
}];
const POSITIVE_CATEGORIES: {
  key: Category;
  label: string;
}[] = [{
  key: "praise",
  label: "Praise"
}, {
  key: "feature",
  label: "Feature request"
}, {
  key: "general",
  label: "General"
}];
const NEGATIVE_CATEGORIES: {
  key: Category;
  label: string;
}[] = [{
  key: "bug",
  label: "Bug"
}, {
  key: "usability",
  label: "Usability"
}, {
  key: "feature",
  label: "Feature request"
}, {
  key: "general",
  label: "General"
}];

// Four tags each so the chip row fits on one line in the feedback panel.
const POSITIVE_TAGS = ["Easy to use", "Fast", "Beautiful", "Helpful"];
const NEGATIVE_TAGS = ["Confusing", "Slow", "Buggy", "Missing features"];
type UsageFreq = "daily" | "weekly" | "first_time" | "rarely";
const USAGE_OPTIONS: {
  key: UsageFreq;
  label: string;
}[] = [{
  key: "daily",
  label: "Daily"
}, {
  key: "weekly",
  label: "Weekly"
}, {
  key: "first_time",
  label: "First time"
}, {
  key: "rarely",
  label: "Rarely"
}];
const MAX_COMMENT_LENGTH = 1000;
const MAX_FILES = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// ============================================================================
// Variant-aware style helpers
// ============================================================================

function v(variant: "sidebar" | "modal", sidebar: string, modal: string) {
  return variant === "modal" ? modal : sidebar;
}

// Mood-dependent chip styling. White background always; the glow, border, and
// text colour track the current mood so the form reads coherently — positive
// moods (love/good) paint green, non-positive (okay/bad) paint red.
function selectedChipClasses(mood: Mood | null): string {
  if (mood === "love" || mood === "good") {
    return "text-green-700 border-green-400/40 shadow-[0_2px_8px_rgba(34,197,94,0.22)]";
  }
  if (mood === "okay" || mood === "bad") {
    return "text-red-700 border-red-400/40 shadow-[0_2px_8px_rgba(239,68,68,0.22)]";
  }
  return "text-cova-brand border-cova-brand/30 shadow-[0_2px_8px_rgba(86,81,255,0.2)]";
}
function unselectedChipClasses(mood: Mood | null): string {
  if (mood === "love" || mood === "good") {
    return "text-gray-600 border-black/8 shadow-xs hover:text-green-700 hover:border-green-300 hover:shadow-[0_2px_8px_rgba(34,197,94,0.18)]";
  }
  if (mood === "okay" || mood === "bad") {
    return "text-gray-600 border-black/8 shadow-xs hover:text-red-700 hover:border-red-300 hover:shadow-[0_2px_8px_rgba(239,68,68,0.18)]";
  }
  return "text-gray-600 border-black/8 shadow-xs hover:text-cova-brand hover:border-cova-brand/20 hover:shadow-[0_2px_8px_rgba(86,81,255,0.15)]";
}

// ============================================================================
// Component
// ============================================================================

interface FeedbackFormProps {
  readonly featureKey?: string | null;
  readonly variant: "sidebar" | "modal";
  readonly isOpen: boolean;
  readonly onSuccess?: () => void;
}
export function FeedbackForm({
  featureKey,
  variant,
  isOpen,
  onSuccess
}: FeedbackFormProps) {
  const [mood, setMood] = useState<Mood | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [impactTags, setImpactTags] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [usageFrequency, setUsageFrequency] = useState<UsageFreq | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  // Computed once at mount rather than in a passive effect. It gates the "or"
  // divider + "Capture screen" button — ~73px — so probing it after mount made the
  // form jump on every single open, and made any measurement of the card taken
  // during the mount commit read short. The check is synchronous; nothing is gained
  // by deferring it. Safe on the server: this form only ever mounts client-side
  // (it lives inside the `isOpen &&` subtree), and the navigator guard holds anyway.
  const [supportsScreenCapture] = useState(() => typeof navigator !== "undefined" && !!navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === "function");
  // The broad area this feedback is about. Pre-filled to the category of the page
  // the user is on, but freely changeable so feedback that isn't about the current
  // page (e.g. billing) can still be filed correctly.
  const [selectedAreaKey, setSelectedAreaKey] = useState<string>(feedbackAreaForRoute(featureKey));
  const {
    track
  } = useAnalytics();
  const {
    submitFeedback,
    isSubmitting
  } = useSubmitFeedback();
  const {
    uploadScreenshots,
    isUploading
  } = useUploadFeedbackScreenshots();
  const setCapturingScreen = useAnnouncementStore(s => s.setCapturingScreen);
  const initialComment = useAnnouncementStore(s_0 => s_0.feedbackInitialComment);
  const moodGroupRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Release the object URL of every preview that leaves the list, whichever
  // path dropped it (close reset, remove button). No state is set here.
  const previewedFilesRef = useRef<FileWithPreview[]>([]);
  useEffect(() => {
    const kept = new Set(files.map(f => f.dataUrl));
    previewedFilesRef.current.forEach(f_0 => {
      if (!kept.has(f_0.dataUrl)) URL.revokeObjectURL(f_0.dataUrl);
    });
    previewedFilesRef.current = files;
  }, [files]);

  // The open/close edge, handled during render. Closing resets the form.
  // Opening pre-fills the area selector with the current page's category and
  // seeds the comment with a caller-supplied template; featureKey is stable
  // while open (navigation closes the panel), so a mid-session change of it
  // re-derives the area without clobbering the comment.
  const [wasOpen, setWasOpen] = useState(false);
  const [areaFeatureKey, setAreaFeatureKey] = useState(featureKey);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setAreaFeatureKey(featureKey);
      setSelectedAreaKey(feedbackAreaForRoute(featureKey));
      if (initialComment) setComment(initialComment);
    } else {
      setMood(null);
      setCategory(null);
      setImpactTags([]);
      setComment("");
      setFiles([]);
      setUsageFrequency(null);
      setShowSuccess(false);
    }
  } else if (isOpen && featureKey !== areaFeatureKey) {
    setAreaFeatureKey(featureKey);
    setSelectedAreaKey(feedbackAreaForRoute(featureKey));
  }

  // Clipboard paste handler
  useEffect(() => {
    if (!isOpen) return;
    function handlePaste(e: ClipboardEvent) {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }
      if (imageFiles.length === 0) return; // allow normal text paste

      e.preventDefault();
      setFiles(prev => {
        if (prev.length >= MAX_FILES) {
          toast.error(`Maximum ${MAX_FILES} screenshots allowed.`);
          return prev;
        }
        const remaining = MAX_FILES - prev.length;
        const toAdd = imageFiles.slice(0, remaining);
        const additions: FileWithPreview[] = [];
        for (const f_1 of toAdd) {
          if (f_1.size > MAX_FILE_SIZE) {
            toast.error(`Pasted image exceeds 5MB limit.`);
            continue;
          }
          additions.push({
            file: f_1,
            dataUrl: URL.createObjectURL(f_1)
          });
        }
        return [...prev, ...additions];
      });
    }
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [isOpen]);

  // Screen capture handler
  async function handleScreenCapture() {
    if (isCapturing) return;
    setIsCapturing(true);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true
      });
      try {
        const track_0 = stream.getVideoTracks()[0];
        const video = document.createElement("video");
        video.srcObject = stream;
        video.muted = true;
        await video.play();

        // Wait a frame to ensure video dimensions are available
        await new Promise(resolve => requestAnimationFrame(resolve));

        // Hide feedback UI so it doesn't appear in the captured frame
        setCapturingScreen(true);
        await new Promise(resolve_0 => setTimeout(resolve_0, 300));
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Could not get canvas context");
        ctx.drawImage(video, 0, 0);

        // Restore UI immediately after capturing the frame
        setCapturingScreen(false);

        // Stop track immediately after drawing
        track_0.stop();
        stream.getTracks().forEach(t_0 => t_0.stop());
        video.srcObject = null;
        const blob = await new Promise<Blob | null>(resolve_1 => canvas.toBlob(resolve_1, "image/png"));
        if (!blob) throw new Error("Could not create image blob");
        if (blob.size > MAX_FILE_SIZE) {
          toast.error("Captured screenshot exceeds 5MB limit.");
          return;
        }
        const fileName = `screenshot-${new Date().toISOString().replace(/[:.]/g, "-")}.png`;
        const file_0 = new File([blob], fileName, {
          type: "image/png"
        });
        setFiles(prev_0 => {
          if (prev_0.length >= MAX_FILES) {
            toast.error(`Maximum ${MAX_FILES} screenshots allowed.`);
            return prev_0;
          }
          return [...prev_0, {
            file: file_0,
            dataUrl: URL.createObjectURL(file_0)
          }];
        });
      } catch (innerError) {
        // Ensure tracks are stopped even on error
        stream.getTracks().forEach(t => t.stop());
        throw innerError;
      }
    } catch (error) {
      // User cancelled the picker - not an error
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        return;
      }
      console.error("[FeedbackForm] Screen capture failed:", error);
      toast.error("Screen capture failed. Please try again or use file upload.");
    } finally {
      setCapturingScreen(false);
      setIsCapturing(false);
    }
  }
  const isPositiveMood = mood === "love" || mood === "good";
  const availableTags = isPositiveMood ? POSITIVE_TAGS : NEGATIVE_TAGS;
  const availableCategories = isPositiveMood ? POSITIVE_CATEGORIES : NEGATIVE_CATEGORIES;

  // Dropdown trigger label: the broad area, plus the specific page in brackets
  // when the picked area still matches the page the user is on — so they know
  // what their feedback is tied to. Drops the bracket once they pick a different area.
  const selectedAreaLabel = FEEDBACK_AREAS.find(a => a.key === selectedAreaKey)?.label ?? resolveAreaLabel(selectedAreaKey);
  const originPageLabel = featureKey && featureKey !== "platform" && feedbackAreaForRoute(featureKey) === selectedAreaKey ? resolveAreaLabel(featureKey) : null;
  const areaTriggerLabel = originPageLabel && originPageLabel !== selectedAreaLabel ? `${selectedAreaLabel} (${originPageLabel})` : selectedAreaLabel;
  function toggleTag(tag: string) {
    setImpactTags(prev_1 => prev_1.includes(tag) ? prev_1.filter(t_1 => t_1 !== tag) : [...prev_1, tag]);
  }
  function handleFileAdd(newFiles: FileList | null) {
    if (!newFiles) return;
    const additions_0: FileWithPreview[] = [];
    for (let i_0 = 0; i_0 < newFiles.length && files.length + additions_0.length < MAX_FILES; i_0++) {
      const f_2 = newFiles[i_0];
      if (f_2.size > MAX_FILE_SIZE) continue;
      if (!f_2.type.startsWith("image/")) continue;
      additions_0.push({
        file: f_2,
        dataUrl: URL.createObjectURL(f_2)
      });
    }
    setFiles(prev_2 => [...prev_2, ...additions_0]);
  }
  function removeFile(index: number) {
    setFiles(prev_3 => {
      const removed = prev_3[index];
      if (removed) URL.revokeObjectURL(removed.dataUrl);
      return prev_3.filter((_, i_1) => i_1 !== index);
    });
  }
  async function handleSubmit() {
    if (!mood || !selectedAreaKey) return;
    // Addon context comes from the actual page the user was on — the selected
    // value is now a broad category, not a specific feature. Safe when there's no
    // page or it isn't an addon: isAddonFeature returns false.
    const finalTags = featureKey && isAddonFeature(featureKey) ? [...impactTags, `addon:${featureKey}`] : impactTags;
    const payload = {
      featureKey: selectedAreaKey,
      // The page the user was actually on (route key), surfaced as "Submitted from".
      originFeatureKey: featureKey ?? undefined,
      mood,
      category,
      impactTags: finalTags,
      comment: comment || undefined,
      hasScreenshots: files.length > 0,
      usageFrequency: usageFrequency || undefined,
      followupConsent: true
    };
    try {
      const result = await submitFeedback(payload);

      // Upload screenshots after feedback is created
      if (files.length > 0) {
        try {
          await uploadScreenshots({
            files: files.map(f_3 => f_3.file),
            feedbackId: result.id
          });
        } catch (uploadError) {
          console.error("[FeedbackForm] Screenshot upload failed:", uploadError);
          toast.error("Feedback submitted, but some screenshots failed to upload.");
        }
      }
      track(getEvent('feature-announcement', 'feedback_submitted'), {
        featureKey: selectedAreaKey,
        // The specific page the user was actually on, so we can see "was on X,
        // filed under category Y".
        originFeatureKey: featureKey ?? undefined,
        areaChanged: selectedAreaKey !== feedbackAreaForRoute(featureKey),
        mood,
        hasCategory: !!category,
        hasComment: !!comment,
        hasScreenshots: files.length > 0,
        impactTagCount: impactTags.length
      });
      setShowSuccess(true);
      setTimeout(() => {
        onSuccess?.();
      }, 2000);
    } catch (error_0) {
      console.error("[FeedbackForm] submitFeedback failed:", error_0);
      toast.error("Failed to submit feedback. Please try again.");
    }
  }

  // Roving tabindex for mood group
  function handleMoodKeyDown(e_0: React.KeyboardEvent, index_0: number) {
    let nextIndex: number | null = null;
    if (e_0.key === "ArrowRight" || e_0.key === "ArrowDown") {
      nextIndex = (index_0 + 1) % MOODS.length;
    } else if (e_0.key === "ArrowLeft" || e_0.key === "ArrowUp") {
      nextIndex = (index_0 - 1 + MOODS.length) % MOODS.length;
    }
    if (nextIndex !== null) {
      e_0.preventDefault();
      const group = moodGroupRef.current;
      if (!group) return;
      const buttons = group.querySelectorAll<HTMLButtonElement>("[role=radio]");
      buttons[nextIndex]?.focus();
      setMood(MOODS[nextIndex].key);
    }
  }

  // Drag-and-drop handlers
  function handleDragOver(e_1: React.DragEvent) {
    e_1.preventDefault();
    e_1.stopPropagation();
  }
  function handleDrop(e_2: React.DragEvent) {
    e_2.preventDefault();
    e_2.stopPropagation();
    handleFileAdd(e_2.dataTransfer.files);
  }
  const charCount = comment.length;
  const charColor = charCount > MAX_COMMENT_LENGTH ? "text-red-500" : charCount > MAX_COMMENT_LENGTH * 0.9 ? "text-amber-500" : "text-gray-400";
  const thumbSize = v(variant, "h-16 w-16", "h-12 w-12");
  return <>
      {/* Success overlay */}
      <AnimatePresence>
        {showSuccess && <motion.div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }}>
            <div className="animate-fbs-success-pop flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
                <path d="M10 20L17 27L30 13" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="60" strokeDashoffset="60" className="animate-fbs-check-draw" />
              </svg>
            </div>
            <p className="mt-4 text-lg font-semibold text-gray-900">
              Thank you!
            </p>
            <p className="mt-1 max-w-[260px] text-center text-sm text-gray-500">
              We read everything and reply when we can, so thanks for your patience.
            </p>
          </motion.div>}
      </AnimatePresence>

      {/* Body */}
      <div className={`flex-1 overflow-y-auto scrollbar-thin ${v(variant, "px-6 py-5", "px-4 py-3")}`}>
        {/* Area selector — what this feedback is about, pre-filled to the current
            page but changeable so off-route feedback (e.g. billing) files correctly. */}
        <div className={v(variant, "mb-6", "mb-3")}>
          <p className={`${v(variant, "mb-3 text-sm", "mb-2 text-xs")} font-medium text-gray-700`}>
            What area is this about?
          </p>
          <Select value={selectedAreaKey} onValueChange={setSelectedAreaKey}>
            <SelectTrigger aria-label="Feedback area" className="w-full bg-white text-[#091E42] data-[state=open]:border-[#5651ff]/30 data-[state=open]:shadow-[0_2px_8px_rgba(86,81,255,0.15)]">
              {/* Custom value so we can append the current page in brackets, e.g.
                  "Product & Features (Assistant)" — the Radix SelectValue can't. */}
              <span data-slot="select-value">{areaTriggerLabel}</span>
            </SelectTrigger>
            {/* Open the menu to the RIGHT of the panel rather than dropping down
                over the form. The trigger is ~full panel width, so anchoring side
                right lands the menu just past the panel's right edge; sideOffset
                clears the body padding. avoidCollisions flips it left if there's no
                room (e.g. the right-docked sidebar variant) so it never goes
                off-screen. Marker class lifts the portaled menu above the z-1500
                panel — see the :has() rule in globals.css (Radix's popper wrapper is
                position:fixed, so the z-index must sit on the wrapper, not here). */}
            <GlassSelectContent className="feedback-area-select-content" side="right" align="start" sideOffset={16} avoidCollisions>
              {FEEDBACK_AREAS.map(a_0 => <SelectItem key={a_0.key} value={a_0.key}>
                  {a_0.label}
                </SelectItem>)}
            </GlassSelectContent>
          </Select>
        </div>

        {/* 1. Mood selector */}
        <div className={v(variant, "mb-6", "mb-3")}>
          <p className={`${v(variant, "mb-3 text-sm", "mb-2 text-xs")} font-medium text-gray-700`}>
            How do you feel about this feature?
          </p>
          <div ref={moodGroupRef} role="radiogroup" aria-label="Mood" className="grid grid-cols-4 gap-2">
            {MOODS.map((m, i_2) => {
            const selected = mood === m.key;
            return <button key={m.key} type="button" role="radio" aria-checked={selected} tabIndex={selected || mood === null && i_2 === 0 ? 0 : -1} onClick={() => {
              setMood(m.key);
              setImpactTags([]); // reset tags on mood change
              setCategory(null); // reset category since available options change
            }} onKeyDown={e_3 => handleMoodKeyDown(e_3, i_2)} className={["flex flex-col items-center gap-1 rounded-xl border bg-white text-sm transition-all active:scale-[0.97]", v(variant, "py-3", "py-2"), selected ? "border-cova-brand/30 shadow-[0_2px_10px_rgba(86,81,255,0.22)]" : "border-black/6 shadow-[0_3px_10px_rgba(100,116,139,0.15)] hover:shadow-[0_4px_14px_rgba(100,116,139,0.22)]"].join(" ")}>
                  <span className={v(variant, "text-2xl", "text-lg")}>{m.emoji}</span>
                  <span className={selected ? "font-medium text-cova-brand" : "text-gray-600"}>
                    {m.label}
                  </span>
                </button>;
          })}
          </div>
        </div>

        {/* 2 + 3. Category + impact tags — both mount after mood selection.
            Wrapped in a single AnimatePresence so the height change feels
            continuous rather than snapping. */}
        <AnimatePresence initial={false}>
          {mood && <motion.div key="mood-dependent" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: "auto",
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} className="overflow-hidden">
              {/* Category */}
              <div className={v(variant, "mb-6", "mb-3")}>
                <p className={`${v(variant, "mb-3 text-sm", "mb-2 text-xs")} font-medium text-gray-700`}>Category</p>
                <div className="flex flex-wrap gap-2">
                  {availableCategories.map(c => {
                const selected_0 = category === c.key;
                return <button key={c.key} type="button" onClick={() => setCategory(selected_0 ? null : c.key)} className={["rounded-full border bg-white transition-all active:scale-[0.97]", v(variant, "px-4 py-1.5 text-sm", "px-3 py-1 text-xs"), selected_0 ? selectedChipClasses(mood) : unselectedChipClasses(mood)].join(" ")}>
                        {c.label}
                      </button>;
              })}
                </div>
              </div>

              {/* What stood out */}
              <div className={v(variant, "mb-6", "mb-3")}>
                <p className={`${v(variant, "mb-3 text-sm", "mb-2 text-xs")} font-medium text-gray-700`}>
                  What stood out? <span className="font-normal text-gray-400">(Select Multiple)</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {availableTags.map(tag_0 => {
                const selected_1 = impactTags.includes(tag_0);
                return <button key={tag_0} type="button" onClick={() => toggleTag(tag_0)} className={["rounded-full border bg-white font-medium transition-all active:scale-[0.97]", v(variant, "px-3 py-1 text-xs", "px-3 py-1 text-xs"), selected_1 ? selectedChipClasses(mood) : unselectedChipClasses(mood)].join(" ")}>
                        {tag_0}
                      </button>;
              })}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* 4. Usage frequency */}
        <div className={v(variant, "mb-6", "mb-3")}>
          <p className={`${v(variant, "mb-3 text-sm", "mb-2 text-xs")} font-medium text-gray-700`}>
            How often do you use this feature?
          </p>
          <div className="flex flex-wrap gap-2">
            {USAGE_OPTIONS.map(opt => {
            const selected_2 = usageFrequency === opt.key;
            return <button key={opt.key} type="button" onClick={() => setUsageFrequency(selected_2 ? null : opt.key)} className={["rounded-full border bg-white transition-all active:scale-[0.97]", v(variant, "px-4 py-1.5 text-sm", "px-3 py-1 text-xs"), selected_2 ? selectedChipClasses(mood) : unselectedChipClasses(mood)].join(" ")}>
                  {opt.label}
                </button>;
          })}
          </div>
        </div>

        {/* 5. Comment */}
        <div className={v(variant, "mb-6", "mb-3")}>
          <div className="flex items-center justify-between">
            <p className={`${v(variant, "mb-2 text-sm", "mb-1 text-xs")} font-medium text-gray-700`}>
              Tell us more (optional)
            </p>
            <span className={`text-xs ${charColor}`}>
              {charCount}/{MAX_COMMENT_LENGTH}
            </span>
          </div>
          <GlassTextarea value={comment} onChange={e_4 => setComment(e_4.target.value)} maxLength={MAX_COMMENT_LENGTH} rows={variant === "modal" ? 2 : 4} placeholder="What's on your mind?" className="px-4 py-3" autoGrowMaxPx={variant === "modal" ? 240 : 320} />
        </div>

        {/* 6. Screenshots */}
        <div className="mb-4">
          <p className={`${v(variant, "mb-2 text-sm", "mb-1 text-xs")} font-medium text-gray-700`}>
            Screenshots (optional)
          </p>
          {/* Thumbnails */}
          {files.length > 0 && <div className="mb-3 flex flex-wrap gap-2">
              {files.map((f_4, i_3) => <div key={i_3} className={`group relative ${thumbSize} overflow-hidden rounded-lg`}>
                  <img src={f_4.dataUrl} alt={`Screenshot ${i_3 + 1}`} className="h-full w-full object-cover" />
                  <button type="button" onClick={() => removeFile(i_3)} aria-label={`Remove screenshot ${i_3 + 1}`} className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100">
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                      <path d="M1 1L7 7M1 7L7 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>)}
            </div>}
          {/* Drop zone + capture */}
          {files.length < MAX_FILES && <div className="flex flex-col gap-2">
              <div onDragOver={handleDragOver} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} role="button" tabIndex={0} onKeyDown={e_5 => {
            if (e_5.key === "Enter" || e_5.key === " ") {
              e_5.preventDefault();
              fileInputRef.current?.click();
            }
          }} className="flex cursor-pointer flex-col items-center gap-1 rounded-xl border-2 border-dashed border-gray-200 px-4 py-4 text-center transition-colors hover:border-[#6366f1]/40 hover:bg-[#6366f1]/5">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span className="text-xs text-gray-500">
                  Drop images, paste, or click to upload (max {MAX_FILES}, 5MB each)
                </span>
              </div>
              {supportsScreenCapture && <>
                  <div className="flex items-center gap-2">
                    <div className="h-px flex-1 bg-gray-200" />
                    <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">or</span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                  <button type="button" onClick={handleScreenCapture} disabled={isCapturing} className={["flex w-full items-center justify-center gap-2 rounded-xl border text-sm font-medium transition-all", v(variant, "py-3", "py-2.5"), isCapturing ? "cursor-wait border-[#6366f1]/20 bg-[#6366f1]/5 text-[#6366f1]/60" : "border-gray-200 bg-white text-gray-700 hover:border-[#6366f1]/40 hover:bg-[#6366f1]/5 hover:text-[#6366f1] active:scale-[0.98]"].join(" ")}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                    {isCapturing ? "Capturing..." : "Capture screen"}
                  </button>
                </>}
            </div>}
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={e_6 => handleFileAdd(e_6.target.files)} />
        </div>
      </div>

      {/* Footer */}
      <div className={`border-t border-gray-100 ${v(variant, "px-6 py-4", "px-4 py-3")}`}>
        <GlassCTAButtonFull type="button" onClick={handleSubmit} disabled={!mood || isSubmitting || isUploading} className={v(variant, "py-3 text-sm", "py-2.5 text-xs")}>
          {isSubmitting ? "Sending..." : isUploading ? "Uploading screenshots..." : "Send Feedback"}
        </GlassCTAButtonFull>
      </div>
    </>;
}